<template>
  <v-card
    class="pc-loader-panel"
    :class="{ 'is-dragging': isDragging }"
    :style="panelStyle"
    elevation="8"
  >
    <!-- Header with Drag Handle -->
    <v-card-title
      class="py-3 px-4 primary white--text drag-handle"
      @mousedown="startDrag"
    >
      <v-icon color="white" class="drag-icon mr-2">mdi-drag</v-icon>
      <v-icon color="white" class="mr-2">mdi-cloud-upload</v-icon>
      <span class="text-subtitle-1">Point Cloud Loader</span>
      <v-spacer></v-spacer>
      <v-btn icon small @click="closePanel" class="ml-2">
        <v-icon color="white">mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-divider></v-divider>

    <!-- Scrollable Content Area -->
    <div class="scrollable-content">
      <v-card-text class="pa-3">

        <!-- Range Slider Section -->
        <div class="range-slider-section mb-4">
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="text-subtitle-2">
              <v-icon small class="mr-1">mdi-radius-outline</v-icon>
              Load Range
            </span>
            <v-chip small :color="hasChanges ? 'warning' : 'primary'" outlined>
              {{ pendingRange.toFixed(1) }}m
            </v-chip>
          </div>

          <v-slider
            v-model="pendingRange"
            :min="0"
            :max="50"
            :step="0.5"
            thumb-label="always"
            color="primary"
            track-color="grey lighten-2"
            @change="saveRangePreference"
          >
            <template v-slot:prepend>
              <span class="text-caption grey--text">0m</span>
            </template>
            <template v-slot:append>
              <span class="text-caption grey--text">30m</span>
            </template>
          </v-slider>

          <div class="text-caption grey--text text-center mt-1">
            <span v-if="hasChanges">
              <v-icon x-small color="warning">mdi-alert-circle</v-icon>
              Pending: {{ pendingRange.toFixed(1) }}m | Current: {{ appliedRange.toFixed(1) }}m
            </span>
            <span v-else>
              Load point clouds within {{ appliedRange.toFixed(1) }}m from center
            </span>
          </div>
        </div>

        <v-divider class="my-3"></v-divider>

        <!-- Point Cloud List Section -->
        <div class="point-cloud-list-section">
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="text-subtitle-2">
              <v-icon small class="mr-1">mdi-format-list-bulleted</v-icon>
              Point Clouds ({{ filteredPointClouds.length }})
            </span>
            <v-chip x-small color="grey" outlined>
              {{ loadedCount }} loaded
            </v-chip>
          </div>

          <!-- Empty State -->
          <v-alert
            v-if="filteredPointClouds.length === 0"
            dense
            text
            type="info"
            icon="mdi-information"
            class="mb-0"
          >
            <span class="text-caption">
              No point clouds available or all are out of range.
            </span>
          </v-alert>

          <!-- Point Cloud List -->
          <v-list v-else dense class="pa-0">
            <v-list-item
              v-for="pcItem in filteredPointClouds"
              :key="pcItem.pc.name"
              class="px-2 py-1 pc-list-item"
            >
              <v-list-item-content class="py-1">
                <v-list-item-title class="text-caption">
                  <div class="d-flex align-center justify-space-between">
                    <span class="pc-name">{{ pcItem.pc.name }}</span>
                    <div class="d-flex align-center">
                      <!-- Primary Badge -->
                      <v-chip
                        v-if="pcItem.isPrimary"
                        x-small
                        color="info"
                        class="ml-2"
                      >
                        <v-icon x-small left>mdi-star</v-icon>
                        Primary
                      </v-chip>
                      <!-- Status Badge -->
                      <v-chip
                        v-else
                        x-small
                        :color="getStatusColor(pcItem.status)"
                        :outlined="pcItem.status !== 'loaded'"
                        class="ml-2"
                      >
                        <v-icon v-if="pcItem.status === 'loading'" x-small left class="rotating">
                          mdi-loading
                        </v-icon>
                        {{ getStatusText(pcItem.status) }}
                      </v-chip>
                    </div>
                  </div>
                </v-list-item-title>
                <v-list-item-subtitle class="text-caption mt-1">
                  <v-icon x-small class="mr-1">mdi-map-marker-distance</v-icon>
                  {{ pcItem.distance.toFixed(2) }}m
                  <span v-if="pcItem.isPrimary">(Center)</span>
                  <span v-else>from center</span>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </div>

      </v-card-text>
    </div>

    <!-- Apply Button -->
    <v-card-actions class="pa-3 pt-0">
      <v-btn
        :disabled="!hasChanges"
        color="primary"
        block
        @click="applyRange"
      >
        <v-icon left>mdi-check</v-icon>
        Apply Changes ({{ appliedRange.toFixed(1) }}m → {{ pendingRange.toFixed(1) }}m)
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { logNavigationStart, logNavigationComplete, logNavigationWarning } from '@/utils/debugLogger'

export default {
  name: 'PointCloudLoaderPanel',

  props: {
    viewer: {
      type: Object,
      required: true
    },
    centerPoint: {
      type: Object,
      required: true
    },
    pointClouds: {
      type: Array,
      default: () => []
    },
    loadedPointClouds: {
      type: Map,
      default: () => new Map()
    },
    loadingPointClouds: {
      type: Set,
      default: () => new Set()
    },
    failedPointClouds: {
      type: Set,
      default: () => new Set()
    },
    primaryPointCloudId: {
      type: [String, Number],
      default: null
    },
    primaryPointCloudName: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      pendingRange: 0,      // Slider value (not yet applied)
      appliedRange: 0,      // Currently applied range
      isDragging: false,
      panelPosition: { x: 100, y: 100 },
      dragOffset: { x: 0, y: 0 }
    }
  },

  computed: {
    panelStyle() {
      return {
        position: 'absolute',
        left: `${this.panelPosition.x}px`,
        top: `${this.panelPosition.y}px`,
        zIndex: 1200
      }
    },

    hasChanges() {
      return this.pendingRange !== this.appliedRange
    },

    filteredPointClouds() {
      const result = []

      // Add primary point cloud first if it exists
      if (this.primaryPointCloudId) {
        result.push({
          pc: {
            id: this.primaryPointCloudId,
            name: this.primaryPointCloudName
          },
          distance: 0,
          status: 'loaded',
          isPrimary: true
        })
      }

      if (!this.centerPoint || !this.pointClouds) {
        return result
      }

      // Calculate distance for each additional point cloud
      const pcWithDistances = this.pointClouds
        .filter(pc => pc.id !== this.primaryPointCloudId && pc.pointCloudsScan) // Exclude primary by ID & check if scan exists
        .map(pc => {
          const distance = this.calculateDistance(pc)
          const status = this.getPointCloudStatus(pc.id)

          return {
            pc,
            distance,
            status,
            isPrimary: false
          }
        })

      // Sort by distance (closest first) and append to result
      const sorted = pcWithDistances.sort((a, b) => a.distance - b.distance)
      result.push(...sorted)

      return result
    },

    loadedCount() {
      return this.filteredPointClouds.filter(item => item.status === 'loaded').length
    }
  },

  mounted() {
    this.loadSavedPreferences()
    this.setupDragListeners()

    // Don't auto-apply saved range - user must explicitly click Apply button
    // The saved range will show as a pending change with Apply button visible
  },

  beforeDestroy() {
    this.removeDragListeners()
  },

  methods: {
    /**
     * Calculate distance from crop box center to point cloud camera position
     */
    calculateDistance(pc) {
      if (!this.centerPoint || !pc) {
        return 0
      }

      try {
        // Use the model's pointCloudsScan getter for robust scan lookup
        const scan = pc.pointCloudsScan

        if (!scan) {
          return 0
        }

        const cameraPos = scan.getCameraPositionInViewer()
        return cameraPos.distanceTo(this.centerPoint)
      } catch (error) {
        logNavigationWarning('PointCloudLoader', {
          message: 'Error calculating distance',
          pcId: pc.id,
          pcName: pc.name,
          error: error.message
        })
        return 0
      }
    },

    /**
     * Get status of a point cloud
     */
    getPointCloudStatus(pcId) {
      const loadedPC = this.loadedPointClouds.get(pcId);
      if (loadedPC) {
        // Check if loaded but hidden
        return loadedPC.visible ? 'loaded' : 'hidden';
      }
      if (this.loadingPointClouds.has(pcId)) {
        return 'loading'
      }
      if (this.failedPointClouds.has(pcId)) {
        return 'failed'
      }
      return 'idle'
    },

    /**
     * Get status color for chip
     */
    getStatusColor(status) {
      const colors = {
        'loaded': 'success',
        'hidden': 'warning',  // Orange/yellow for hidden
        'loading': 'info',
        'failed': 'error',
        'idle': 'grey'
      }
      return colors[status] || 'grey'
    },

    /**
     * Get status text
     */
    getStatusText(status) {
      const texts = {
        'loaded': 'Loaded',
        'hidden': 'Hidden',  // New status for loaded but invisible
        'loading': 'Loading',
        'failed': 'Failed',
        'idle': 'Idle'
      }
      return texts[status] || 'Idle'
    },

    /**
     * Apply pending range changes - emit event to parent for loading/unloading
     */
    applyRange() {
      logNavigationStart('PointCloudRangeApply', {
        oldRange: this.appliedRange,
        newRange: this.pendingRange
      })

      this.$emit('range-changed', this.pendingRange)

      // Update applied range to match pending
      this.appliedRange = this.pendingRange

      logNavigationComplete('PointCloudRangeApply', {
        range: this.appliedRange,
        filteredCount: this.filteredPointClouds.filter(item => item.distance <= this.appliedRange).length
      })
    },

    /**
     * Save range preference to localStorage
     */
    saveRangePreference() {
      try {
        localStorage.setItem('potree-pc-loader-range-preference', this.pendingRange.toString())
        logNavigationComplete('SaveRangePreference', {
          range: this.pendingRange
        })
      } catch (error) {
        logNavigationWarning('SaveRangePreference', {
          message: 'Failed to save range preference',
          error: error.message
        })
      }
    },

    /**
     * Load saved preferences from localStorage
     */
    loadSavedPreferences() {
      try {
        // Load range preference
        const savedRange = localStorage.getItem('potree-pc-loader-range-preference')
        if (savedRange !== null) {
          const range = parseFloat(savedRange)
          this.pendingRange = range
          this.appliedRange = 0  // Keep applied at 0 - user must click Apply button
        }

        // Load panel position
        const savedPosition = localStorage.getItem('potree-pc-loader-panel-position')
        if (savedPosition) {
          this.panelPosition = JSON.parse(savedPosition)
        }

        logNavigationComplete('LoadPreferences', {
          pendingRange: this.pendingRange,
          appliedRange: this.appliedRange,
          position: this.panelPosition
        })
      } catch (error) {
        logNavigationWarning('LoadPreferences', {
          message: 'Failed to load preferences',
          error: error.message
        })
      }
    },

    /**
     * Save panel position to localStorage
     */
    savePanelPosition() {
      try {
        localStorage.setItem('potree-pc-loader-panel-position', JSON.stringify(this.panelPosition))
      } catch (error) {
        logNavigationWarning('SavePanelPosition', {
          message: 'Failed to save panel position',
          error: error.message
        })
      }
    },

    /**
     * Close panel
     */
    closePanel() {
      this.$emit('close')
    },

    // Drag Methods
    startDrag(event) {
      // Prevent dragging if clicking on buttons or other interactive elements
      if (event.target.closest('.v-btn') || event.target.closest('button')) {
        return
      }

      this.isDragging = true
      this.dragOffset.x = event.clientX - this.panelPosition.x
      this.dragOffset.y = event.clientY - this.panelPosition.y

      event.preventDefault()
      event.stopPropagation()
    },

    handleDrag(event) {
      if (!this.isDragging) return

      this.panelPosition.x = event.clientX - this.dragOffset.x
      this.panelPosition.y = event.clientY - this.dragOffset.y

      event.preventDefault()
    },

    stopDrag() {
      if (this.isDragging) {
        this.isDragging = false
        this.savePanelPosition()
      }
    },

    setupDragListeners() {
      document.addEventListener('mousemove', this.handleDrag)
      document.addEventListener('mouseup', this.stopDrag)
    },

    removeDragListeners() {
      document.removeEventListener('mousemove', this.handleDrag)
      document.removeEventListener('mouseup', this.stopDrag)
    }
  }
}
</script>

<style scoped lang="scss">
.pc-loader-panel {
  width: 400px;
  max-width: 90vw;
  border-radius: 8px !important;
  transition: box-shadow 0.2s ease;
  overflow: hidden;
  max-height: 650px;
  height: auto !important;
}

.pc-loader-panel.is-dragging {
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.3) !important;
  cursor: grabbing !important;
  user-select: none;
}

/* Drag Handle */
.drag-handle {
  cursor: grab;
  user-select: none;
  transition: background-color 0.2s ease;
}

.drag-handle:hover {
  background-color: var(--v-primary-darken1) !important;
}

.drag-handle:active {
  cursor: grabbing;
}

.drag-icon {
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.drag-handle:hover .drag-icon {
  opacity: 1;
}

/* Scrollable Content Area */
.scrollable-content {
  max-height: 350px;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

/* Custom Scrollbar Styling */
.scrollable-content::-webkit-scrollbar {
  width: 8px;
}

.scrollable-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.scrollable-content::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.scrollable-content::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Range Slider Section */
.range-slider-section {
  .v-slider {
    margin-top: 8px;
  }
}

/* Point Cloud List */
.pc-list-item {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }

  &:last-child {
    border-bottom: none;
  }
}

.pc-name {
  font-weight: 500;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Loading Animation */
@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.rotating {
  animation: rotate 1s linear infinite;
}

/* Responsive Adjustments */
@media (max-width: 960px) {
  .pc-loader-panel {
    width: 350px;
  }

  .scrollable-content {
    max-height: 300px;
  }
}
</style>
